import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  EffectCoverflow,
  Autoplay,
  Pagination,
  Navigation,
} from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { ArrowUpRight } from "lucide-react";
import categories_book_1 from "../assets/categories_books/categories_book_1.png";
import categories_book_2 from "../assets/categories_books/categories_book_2.png";
import categories_book_3 from "../assets/categories_books/categories_book_3.png";
import categories_book_4 from "../assets/categories_books/categories_book_4.png";
import categories_book_5 from "../assets/categories_books/categories_book_5.png";
import categories_book_6 from "../assets/categories_books/categories_book_6.png";
import categories_book_7 from "../assets/categories_books/categories_book_7.png";
import categories_book_8 from "../assets/categories_books/categories_book_8.png";
import categories_book_9 from "../assets/categories_books/categories_book_9.png";
import { useI18n } from "../i18n/I18nProvider";

export default function Pillars() {
  const { t } = useI18n();
  const categories = [
    {
      image: categories_book_1,
      link: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/iEABchonSNO0By5bDxjBfA",
      i18nKey: "pillars.categories.narrativeFiction",
    },
    {
      image: categories_book_2,
      link: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/ng4QzPE8RTuVCQ9JHKglfw",
      i18nKey: "pillars.categories.memoir",
    },
    {
      image: categories_book_3,
      link: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/LcNDdk.5S62tyh-dcfDN7g",
      i18nKey: "pillars.categories.philosopherThinker",
    },
    {
      image: categories_book_4,
      link: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/y2YkNzeCTxucLSxUB.XSUg",
      i18nKey: "pillars.categories.literatureArtsExpression",
    },
    {
      image: categories_book_5,
      link: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/Lwmr19.wRwOWSVRC43LzlQ",
      i18nKey: "pillars.categories.businessEconomicsSociety",
    },
    {
      image: categories_book_6,
      link: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/t0JlfcUQSE2L-mjjYB9DnA",
      i18nKey: "pillars.categories.cultureHistoryCivilization",
    },
    {
      image: categories_book_7,
      link: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/NQEqBuhOSTG8NcWevIk2bQ",
      i18nKey: "pillars.categories.scienceTechnologyFuture",
    },
    {
      image: categories_book_8,
      link: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/ZbZcNYTVSq-7I1tA2zFYxg",
      i18nKey: "pillars.categories.psychologyHumanMind",
    },
    {
      image: categories_book_9,
      link: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/ndSggHS6QCmA4cbbuBNRiQ",
      i18nKey: "pillars.categories.philosophyEthicsThought",
    },
  ];

  // keep track of active slide (realIndex) so we can compute distance to each slide
  const [active, setActive] = useState(Math.floor(categories.length / 2));

  // helper: minimal circular distance on a looped array
  const circularDistance = (a, b, n) => {
    const d = Math.abs(a - b);
    return Math.min(d, n - d);
  };

  return (
    <section
      id="categories"
      className="pb-12 py-16 lg:py-24 lg:pb-12 bg-[#004668] text-white"
    >
      {/* Header */}
      <div className="text-center px-4 mb-2">
        <h2 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl italic font-bold text-[#F1AF38] mb-4 sm:mb-6">
          {t("pillars.heading")}
        </h2>
        <p className="text-base sm:text-lg lg:text-xl text-white/80 max-w-4xl mx-auto leading-relaxed px-4">
          {t("pillars.description")}
        </p>
      </div>

      {/* Coverflow Slider Container */}
      <div className="relative slider-container">
        <Swiper
          effect="coverflow"
          grabCursor={true}
          centeredSlides={true}
          centeredSlidesBounds={true}
          slidesPerView="auto"
          spaceBetween={-22}
          loop={true}
          loopedSlides={categories.length}
          speed={600}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          coverflowEffect={{
            rotate: 30,
            stretch: 0,
            depth: 120,
            modifier: 1,
            slideShadows: false,
          }}
          pagination={{ clickable: true, dynamicBullets: true }}
          navigation={true}
          modules={[EffectCoverflow, Autoplay, Pagination, Navigation]}
          className="pillars-coverflow-swiper"
          // update active index on slide change (realIndex accounts for loop)
          onSlideChange={(swiper) => setActive(swiper.realIndex)}
          onSwiper={(swiper) => setActive(swiper.realIndex)}
        >
          {categories.map((category, i) => (
            <SwiperSlide key={i}>
              {() => {
                const n = categories.length;
                const dist = circularDistance(i, active, n);
                const isCenter = dist === 0;

                /*
                  Mapping strategy (tweak numbers here):
                  - dist === 0 : center -> largest & brightest, with breathing room
                  - dist === 1 : immediate neighbor -> noticeably smaller & dimmer
                  - dist === 2 : further neighbor -> even smaller & more faded
                  - else : background -> smallest & most faded
                */
                let scale = 0.6;
                let zIndex = 6;
                let opacity = 0.28;
                let translateY = 26;

                if (dist === 0) {
                  scale = 0.96; // center largest
                  zIndex = 50;
                  opacity = 1;
                  translateY = 0;
                } else if (dist === 1) {
                  scale = 0.7;
                  zIndex = 30;
                  opacity = 0.52;
                  translateY = 18;
                } else if (dist === 2) {
                  scale = 0.65;
                  zIndex = 20;
                  opacity = 0.4;
                  translateY = 22;
                }

                const baseShadow = "0 18px 38px rgba(0, 0, 0, 0.45)";
                const activeOutline =
                  "0 0 0 4px rgba(241, 175, 56, 0.95), 0 28px 70px rgba(0, 0, 0, 0.65)";

                return (
                  <div className="slide-inner" style={{ maxWidth: 360 }}>
                    <img
                      src={category.image}
                      alt={`Category ${i + 1}`}
                      draggable="false"
                      style={{
                        transform: `perspective(1200px) translateY(${translateY}px) scale(${scale})`,
                        transition:
                          "transform 420ms cubic-bezier(.2,.9,.2,1), opacity 320ms, box-shadow 320ms, filter 320ms",
                        zIndex,
                        opacity,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        borderRadius: 14,
                        boxShadow: isCenter ? activeOutline : baseShadow,
                        filter: isCenter
                          ? "none"
                          : "grayscale(0.4) saturate(0.7)",
                      }}
                    />
                  </div>
                );
              }}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Bottom Content */}
      <div className="text-center mt-4 px-4">
        <h3 className="text-3xl sm:text-4xl font-bold sm:mb-3 mb-2">
          {t(`${categories[active % categories.length].i18nKey}.name`)}
        </h3>
        <p className="text-base sm:text-lg lg:text-xl font-thin tracking-wider text-white/70 mb-4">
          {t(`${categories[active % categories.length].i18nKey}.description`)}
        </p>
        <div className="inline-flex items-center gap-0 md:self-start justify-center">
          <a
            href={categories[active % categories.length].link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-0"
          >
            <span className="inline-flex items-center gap-2 px-8 py-3 bg-ekatraOrange text-white rounded-full text-lg font-semibold shadow-md hover:bg-ekatraOrange/80 transition">
              {t("pillars.cta")}
            </span>
            <span className="relative left-[-8px] w-[52px] h-[52px] bg-ekatraOrange text-white rounded-full shadow-md flex items-center justify-center hover:bg-ekatraOrange/80 transition">
              <ArrowUpRight size={22} strokeWidth={2} />
            </span>
          </a>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .slider-container {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 28px;
          overflow: visible;
        }

        .pillars-coverflow-swiper {
          width: 100%;
          padding-top: 20px;
          padding-bottom: 80px;
          overflow: visible;
        }

        /* fixed slide width so Swiper centering stays correct */
        .pillars-coverflow-swiper .swiper-slide {
          width: 360px !important;
          height: 480px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border-radius: 12px;
          overflow: visible;
          will-change: transform;
        }

        .pillars-coverflow-swiper .swiper-slide .slide-inner {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: visible;
        }

        .pillars-coverflow-swiper .swiper-slide img {
          border-radius: 12px;
          will-change: transform, opacity;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        /* Navigation buttons */
        .pillars-coverflow-swiper .swiper-button-next,
        .pillars-coverflow-swiper .swiper-button-prev {
          color: #f1af38;
          background: rgba(255, 255, 255, 0.06);
          width: 50px;
          height: 50px;
          border-radius: 50%;
          backdrop-filter: blur(10px);
        }

        .pillars-coverflow-swiper .swiper-button-next:hover,
        .pillars-coverflow-swiper .swiper-button-prev:hover {
          background: rgba(241, 175, 56, 0.12);
        }

        /* Pagination bullets */
        .pillars-coverflow-swiper .swiper-pagination-bullet {
          width: 12px;
          height: 12px;
          background: rgba(255, 255, 255, 0.5);
          opacity: 1;
        }

        .pillars-coverflow-swiper .swiper-pagination-bullet-active {
          background: #f1af38;
          width: 14px;
          height: 14px;
        }

        /* Responsive breakpoints (tweak to taste) */
        @media (max-width: 1024px) {
          .pillars-coverflow-swiper .swiper-slide {
            width: 300px !important;
            height: 420px;
          }
        }

        @media (max-width: 768px) {
          .pillars-coverflow-swiper .swiper-slide {
            width: 240px !important;
            height: 360px;
          }
        }

        @media (max-width: 480px) {
          .pillars-coverflow-swiper .swiper-slide {
            width: 200px !important;
            height: 300px;
          }

          .pillars-coverflow-swiper .swiper-button-next,
          .pillars-coverflow-swiper .swiper-button-prev {
            display: none;
          }
        }
      `}</style>
    </section>
  );
}
